import { Option } from '@sapphire/result';
import { Nullable, pinnedCountryPriority } from '@ps/utils';
import { SurveyLocaleData, SurveyMetadataFormState } from '@ps/survey-utils';
import {
  DEFAULT_CHILD_AUDIENCE_STATE,
  DEFAULT_SURVEY_CHILD_KEY,
} from '../../survey-creation-state.constants';
import {
  SurveyCreationAudienceState,
  SurveyCreationChildAudienceState,
} from '../../survey-creation-state.model';
import { SurveyChildKey } from '../../survey-creation-shared-state.model';
import {
  buildLocaleKeyedChildren,
  buildWaveKeyedChildren,
  ChildMapPatch,
  patchChildrenMap,
  resolveActiveChildKey as resolveActiveChildKeyOf,
  SurveyChildEntry,
  SurveyChildScope,
} from '../../survey-creation-child-registry.helper';

/**
 * A fresh audience state targeting `rootSurveyId` — mirrors
 * `initializeCoreState`: the one child is keyed by the root id directly in
 * edit mode, else the empty-string pre-draft placeholder.
 */
export function initializeAudienceState(
  rootSurveyId: Nullable<number>,
): SurveyCreationAudienceState {
  const childKey = rootSurveyId ?? DEFAULT_SURVEY_CHILD_KEY;

  return {
    children: {
      [childKey]: DEFAULT_CHILD_AUDIENCE_STATE,
    },
  };
}

export function isLoadedAudienceSibling(
  children: Record<SurveyChildKey, SurveyCreationChildAudienceState>,
  surveyId: number,
): boolean {
  return !!children[surveyId] && Object.keys(children).length > 1;
}

export function resolveActiveChildKey(
  state: SurveyCreationAudienceState,
  activeChildKey: Nullable<SurveyChildKey>,
): SurveyChildKey {
  return resolveActiveChildKeyOf(
    state.children,
    activeChildKey,
    DEFAULT_SURVEY_CHILD_KEY,
  );
}

export function resolveTargetChildKeys(
  state: SurveyCreationAudienceState,
  activeChildId: Nullable<number>,
): SurveyChildKey[] {
  return Option.from(activeChildId)
    .map((id) => [id])
    .unwrapOr(Object.keys(state.children));
}

export type ChildPatch = ChildMapPatch<SurveyCreationChildAudienceState>;

export function patchChildren(
  state: SurveyCreationAudienceState,
  activeChildKey: Nullable<SurveyChildKey>,
  scope: SurveyChildScope,
  patch: ChildPatch,
): SurveyCreationAudienceState {
  return {
    ...state,
    children: patchChildrenMap(
      state.children,
      activeChildKey,
      scope,
      patch,
      DEFAULT_CHILD_AUDIENCE_STATE,
    ),
  };
}

export function buildTrackerPreDraftChildren(
  children: Record<SurveyChildKey, SurveyCreationChildAudienceState>,
  numberOfWaves: number,
): Record<SurveyChildKey, SurveyCreationChildAudienceState> {
  const sharedMetadata =
    Object.values(children)[0]?.metadata ??
    DEFAULT_CHILD_AUDIENCE_STATE.metadata;

  return buildWaveKeyedChildren(children, numberOfWaves, () => ({
    ...DEFAULT_CHILD_AUDIENCE_STATE,
    metadata: sharedMetadata,
  }));
}

export function buildMcPreDraftChildren(
  children: Record<SurveyChildKey, SurveyCreationChildAudienceState>,
  localeData: SurveyLocaleData[],
): Record<SurveyChildKey, SurveyCreationChildAudienceState> {
  const sharedMetadata =
    Object.values(children)[0]?.metadata ??
    DEFAULT_CHILD_AUDIENCE_STATE.metadata;

  return buildLocaleKeyedChildren(
    children,
    localeData,
    DEFAULT_SURVEY_CHILD_KEY,
    (locale) => ({
      ...DEFAULT_CHILD_AUDIENCE_STATE,
      metadata: locale
        ? filterMetadataToLocale(
            sharedMetadata,
            locale.countryCode,
            locale.languageShortCode,
          )
        : sharedMetadata,
    }),
  );
}

function filterMetadataToLocale(
  metadata: SurveyMetadataFormState,
  countryCode: string,
  languageShortCode: string,
): SurveyMetadataFormState {
  return {
    ...metadata,
    value: metadata.value && {
      ...metadata.value,
      selectedCountries: [countryCode],
      selectedLanguages: [languageShortCode],
    },
  };
}

// Applies the edit but keeps the child's own selectedCountries/selectedLanguages.
export function preserveChildLocale(
  metadata: SurveyMetadataFormState,
  current: SurveyMetadataFormState,
): SurveyMetadataFormState {
  if (!metadata.value || !current.value) {
    return metadata;
  }

  return {
    ...metadata,
    value: {
      ...metadata.value,
      selectedCountries: current.value.selectedCountries,
      selectedLanguages: current.value.selectedLanguages,
    },
  };
}

export function omitChild<TChild>(
  children: Record<SurveyChildKey, TChild>,
  surveyId: number,
): Record<SurveyChildKey, TChild> {
  return Object.fromEntries(
    Object.entries(children).filter(([key]) => Number(key) !== surveyId),
  );
}

export function firstChildEntry(
  children: readonly SurveyChildEntry[],
): Option<SurveyChildEntry> {
  const ordered = children
    .flatMap((entry) =>
      entry[2].survey ? [[entry, entry[2].survey] as const] : [],
    )
    .sort(
      ([, left], [, right]) =>
        pinnedCountryPriority(left.countryCode) -
          pinnedCountryPriority(right.countryCode) ||
        left.countryName.localeCompare(right.countryName) ||
        left.languageShortCode.localeCompare(right.languageShortCode),
    );

  return Option.from(ordered[0]).map(([entry]) => entry);
}

export function firstChildSurveyId(
  children: readonly SurveyChildEntry[],
): Option<number> {
  return firstChildEntry(children).andThen(([, , { survey }]) =>
    Option.from(survey).map(({ id }) => id),
  );
}
